import React from 'react';
import { UseFormRegister, FieldErrors, Control, useFieldArray, useWatch } from 'react-hook-form';

interface CurrencyOption {
  value: string;
  label: string;
}

interface FOAAmountsSectionProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  control: Control<any>;
  currencyOptions: CurrencyOption[];
  /** Resolves a currency id to its code (e.g. 'USD'); used to show the rate input for non-EGP rows */
  resolveCurrencyCode: (currencyId: string) => string;
  /** Suggested rate to EGP for a currency id (used as placeholder) */
  getSuggestedRate: (currencyId: string) => number | undefined;
}

const FOAAmountsSection: React.FC<FOAAmountsSectionProps> = ({
  register, errors, control, currencyOptions, resolveCurrencyCode, getSuggestedRate
}) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'amounts' });
  const watchedAmounts = useWatch({ control, name: 'amounts' }) || [];

  return (
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Amounts & Currencies</h3>
        <button
          type="button"
          onClick={() => append({ amount: 0, currencyId: '' })}
          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
        >
          + Add Amount
        </button>
      </div>
      <div className="space-y-4">
        {fields.map((field, index) => {
          const rowErrors = (errors as any)?.amounts?.[index];
          const rowCurrencyId = watchedAmounts[index]?.currencyId || '';
          const rowCode = rowCurrencyId ? resolveCurrencyCode(rowCurrencyId) : '';
          const needsRate = !!rowCode && rowCode !== 'EGP';
          const suggestedRate = needsRate ? getSuggestedRate(rowCurrencyId) : undefined;
          return (
            <div key={field.id} className={`grid grid-cols-1 gap-4 items-end ${needsRate ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount {index + 1} {index === 0 && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register(`amounts.${index}.amount`, index === 0 ? {
                    required: 'Amount 1 is required',
                    min: { value: 0, message: 'Amount cannot be negative' }
                  } : {
                    min: { value: 0, message: 'Amount cannot be negative' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
                {rowErrors?.amount && (
                  <p className="mt-1 text-sm text-red-600">{rowErrors.amount.message}</p>
                )}
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency {index + 1} {index === 0 && <span className="text-red-500">*</span>}
                </label>
                <select
                  {...register(`amounts.${index}.currencyId`, index === 0 ? {
                    required: 'Currency 1 is required'
                  } : {})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {currencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {rowErrors?.currencyId && (
                  <p className="mt-1 text-sm text-red-600">{rowErrors.currencyId.message}</p>
                )}
              </div>
              {needsRate && (
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate ({rowCode} to EGP) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register(`amounts.${index}.rate`, {
                      required: `Rate is required for ${rowCode}`,
                      min: { value: 0.01, message: 'Rate must be greater than 0' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={suggestedRate ? String(suggestedRate) : 'e.g., 50'}
                  />
                  {rowErrors?.rate && (
                    <p className="mt-1 text-sm text-red-600">{rowErrors.rate.message}</p>
                  )}
                </div>
              )}
              <div className="md:col-span-1 flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {index === 0 ? 'Primary amount' : `Additional amount ${index + 1}`}
                </span>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FOAAmountsSection;
